<template>
  <b-modal
    id="ConfigureReportsModal"
    size="xl"
    content-class="your-class"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @hidden="doCancel"
  >
    <template
      slot="modal-header"
      slot-scope="{ close }"
    >
      <h5>{{ pageTitle }}</h5>
      <button
        class="btn btn--close"
        @click="close()"
      >
        <font-awesome-icon
          :icon="['fal', 'times']"
          class="icon alt"
        />
      </button>
    </template>

    <form-component>
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <b-card no-body>
          <b-tabs card>
            <b-tab
              title="Report Configuration"
              active
            >
              <b-card-text>
                <input-select
                  id="enabled"
                  v-model="enabled"
                  v-validate="'required'"
                  name="enabled"
                  data-vv-name="enabled"
                  data-vv-as="Email Reports"
                  label="Email Reports"
                  :required="true"
                  :error="errors.first('enabled')"
                  :options="statusOptions"
                />

                <input-select
                  v-if="organizations && $store.getters.isKgbAdminUser"
                  id="organization_id"
                  v-model="organization_id"
                  v-validate="'required'"
                  name="organization_id"
                  data-vv-name="organization"
                  data-vv-as="Organization"
                  label="Organization"
                  :disabled="id >= 0"
                  :required="true"
                  :error="errors.first('organization_id')"
                  display-field="name"
                  :options="organizations"
                  :on-change="updateOrganization"
                />

                <input-select
                  v-if="sites"
                  id="site_id"
                  v-model="site_id"
                  v-validate="'required'"
                  name="site_id"
                  data-vv-name="site_id"
                  data-vv-as="Site"
                  label="Site"
                  :disabled="id >= 0"
                  :required="true"
                  :error="errors.first('site_id')"
                  display-field="name"
                  :options="sites"
                />

                <input-text
                  id="name"
                  v-model="name"
                  v-validate="'required'"
                  name="name"
                  data-vv-name="name"
                  data-vv-as="Name"
                  label="Name"
                  type="text"
                  required
                  :error="errors.first('name')"
                />

                <input-select
                  v-if="$store.getters.isKgbAdminUser"
                  id="internalOnly"
                  v-model="internalOnly"
                  v-validate="'required'"
                  name="internalOnly"
                  data-vv-name="internalOnly"
                  data-vv-as="Report Access"
                  label="Report Access"
                  :required="true"
                  :error="errors.first('internalOnly')"
                  :options="internalOnlyOptions"
                />
              </b-card-text>

              <div>
                <responsive-table>
                  <thead class="inline-header">
                    <tr>
                      <th>Report Schedule</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="scheduleItem of reportSchedule"
                      :key="scheduleItem.id"
                    >
                      <td>{{ parseScheduleText(scheduleItem) }} </td>
                      <td class="actions">
                        <button
                          v-b-tooltip.hover
                          class="red-hover"
                          title="Delete"
                          type="button"
                          @click="deleteSchedule(scheduleItem)"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'trash']"
                            class="icon alt"
                          />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </responsive-table>
              </div>

              <h6>Add Schedule Item</h6>
              <div class="d-flex align-items-center">
                <input-select
                  id="schedule_type"
                  v-model="scheduleType"
                  name="schedule_type"
                  data-vv-as="Schedule Type"
                  label="Schedule Type"
                  :options="availableScheduleTypes"
                  key-field="key"
                  display-field="value"
                  class="p-2"
                />

                <input-text
                  v-if="scheduleType === 'day'"
                  id="dayOfMonth"
                  v-model="selectedDayOfMonth"
                  v-validate="'required'"
                  name="selectedDayOfMonth"
                  data-vv-name="day of month"
                  data-vv-as="day of moth"
                  label="Day Of Month"
                  type="number"
                  max="31"
                  maxlength="2"
                  required
                  :error="errors.first('name')"
                />

                <b-form-group
                  v-if="scheduleType === 'weekly'"
                  v-slot="{ ariaDescribedby }"
                  class="p-2"
                >
                  <b-form-checkbox-group
                    id="daysOfWeek"
                    v-model="selectedDaysOfWeek"
                    :options="daysOfWeek"
                    :aria-describedby="ariaDescribedby"
                    name="daysOfWeek"
                  />
                </b-form-group>

                <button
                  v-if="scheduleType"
                  class="btn btn--primary inline-button"
                  type="button"
                  :disabled="!validSchedule()"
                  @click="addSchedule()"
                >
                  Add
                </button>
              </div>
            </b-tab>

            <b-tab title="Recipients">
              <b-card-text>
                <div>
                  <responsive-table>
                    <thead class="inline-header">
                      <tr>
                        <th>Emails to Receive Report</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="email in emails"
                        :key="email"
                      >
                        <td>{{ email }}</td>
                        <td class="actions">
                          <button
                            v-if="$store.getters.isAdminUser"
                            v-b-tooltip.hover
                            class="red-hover"
                            title="Delete"
                            type="button"
                            @click="deleteEmail(email)"
                          >
                            <font-awesome-icon
                              :icon="['fas', 'trash']"
                              class="icon alt"
                            />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </responsive-table>
                </div>
                <div class="d-flex align-items-center">
                  <input-text
                    id="add_email"
                    v-model="addEmail"
                    name="add_email"
                    data-vv-as="Add Email"
                    label="Add Email"
                    type="email"
                    key-field="id"
                    display-field="full_name"
                  />
                  <button
                    title="Add"
                    type="button"
                    class="btn btn--primary inline-button"
                    :disabled="!validEmail()"
                    @click="addEmailAddress()"
                  >
                    Add
                  </button>
                </div>
              </b-card-text>
            </b-tab>

            <b-tab title="Devices">
              <b-card-text>
                <h5>Devices in Report</h5>
                <div>
                  <responsive-table>
                    <thead class="inline-header">
                      <tr>
                        <th>Serial Number</th>
                        <th>Included</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="device in siteDevices"
                        :key="device.id"
                      >
                        <td>{{ device.serial_number }}</td>
                        <td>
                          <b-form-checkbox
                            v-model="device.onReport"
                            :name="'enabled-' + device.id"
                            value="true"
                            unchecked-value="false"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </responsive-table>
                </div>
              </b-card-text>
            </b-tab>

            <b-tab title="Usage Types">
              <b-card-text>
                <h5>Included usage types in report</h5>
                <b-form-checkbox-group
                  id="usage-type-group"
                  v-model="includedUsageTypes"
                >
                  <div
                    v-for="usageType in availableUsageTypes"
                    :key="usageType.key"
                    class="checkbox-holder"
                  >
                    <b-form-checkbox
                      :value="usageType.key"
                    >
                      {{ usageType.value }}
                    </b-form-checkbox>
                  </div>
                </b-form-checkbox-group>
              </b-card-text>
            </b-tab>
          </b-tabs>
        </b-card>
      </form>
    </form-component>

    <template
      slot="modal-footer"
    >
      <button
        class="btn"
        type="button"
        @click="doCancel()"
      >
        Cancel
      </button>
      <button
        class="btn btn--primary"
        type="button"
        :disabled="saving"
        @click="save()"
      >
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Save</span>
      </button>
    </template>
  </b-modal>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import UserClient from '@/api/UserClient';
import FormComponent from '@/components/FormComponent.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';

const includeAllSitesId = '-100';
const defaultSites = [{ id: includeAllSitesId, name: 'All Sites' }, { id: '-1', name: 'Primary' }];


export default {
  name: 'ConfigureReportsModal',
  components: {
    FormComponent,
    ResponsiveTable,
    InputText,
    InputSelect,
  },
  props: {
    doCancel: {
      type: Function,
    },
    reportData: {
      type: Number,
    },
    userClient: {
      type: UserClient,
    },
    doSave: {
      type: Function,
    },
  },
  data: () => ({
    id: -1,
    enabled: 'true',
    internalOnly: 'false',
    emails: [],
    saving: false,
    statusOptions: [
      { id: 'true', display_label: 'Enabled' },
      { id: 'false', display_label: 'Disabled' },
    ],
    internalOnlyOptions: [
      { id: 'true', display_label: 'Not Visible to Organization' },
      { id: 'false', display_label: 'Visible/Editable to Organization' },
    ],
    site_id: '-1',
    siteName: '',
    organization_id: -1,
    organizations: [],
    sites: [],
    name: '',
    organizationName: '',
    addEmail: '',
    ignoredDeviceIds: [],
    scheduleType: '',
    availableScheduleTypes: [
      { key: 'day', value: 'Specific Day' },
      { key: 'weekly', value: 'Weekly' },
      { key: 'instant', value: 'Instantly' },
    ],
    availableUsageTypes: [
      { key: 'download', value: 'Downloads' },
      { key: 'monitor', value: 'Monitors' },
    ],
    includedUsageTypes: ['download', 'monitor'],
    daysOfWeek: [
      { value: 6, text: 'Sunday' },
      { value: 0, text: 'Monday' },
      { value: 1, text: 'Tuesday' },
      { value: 2, text: 'Wednesday' },
      { value: 3, text: 'Thursday' },
      { value: 4, text: 'Friday' },
      { value: 5, text: 'Saturday' },
    ],
    selectedDaysOfWeek: [],
    selectedDayOfMonth: '',
    reportSchedule: [],
    devices: [],
  }),
  computed: {
    pageTitle() {
      if (this.id === -1) {
        return 'Creating New Report';
      }
      return `Configure Reporting for ${this.organizationName}`;
    },
    siteDevices() {
      return this.devices
        .filter(device => (this.site_id === '-1' && device.site === null) || (device.site && device.site.id === Number(this.site_id)) || this.site_id === includeAllSitesId);
    },
  },
  methods: {
    save() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;
          this.doSave(this.id, {
            enabled: this.enabled === 'true',
            schedules: this.reportSchedule,
            internalOnly: this.internalOnly === 'true',
            organization_id: this.organization_id ? Number(this.organization_id) : null,
            site_id: (Number(this.site_id) < 0) ? null : Number(this.site_id),
            includeAllSites: this.site_id === includeAllSitesId,
            name: this.name,
            emails: this.emails,
            ignored: this.devices.filter(d => d.onReport === 'false').map(d => d.id),
            ignoredUsageTypes: this.availableUsageTypes.map(x => x.key)
              .filter(x => !this.includedUsageTypes.includes(x)),
          }, () => {
            this.saving = false;
          });
        }
      });
    },

    setModal(model, organizations) {
      this.organizations = organizations.filter(x => !x.is_removed);
      this.saving = false;
      this.id = model.id || -1;
      this.enabled = (model.enabled || this.id === -1) ? 'true' : 'false';

      this.organization_id = `${model.organization_id || this.$store.getters.currentUser.organization_id || null}`;
      this.site_id = model.includeAllSites ? includeAllSitesId : `${model.site_id || '-1'}`;
      this.name = model.name || '';
      this.organizationName = model.organizationName;
      this.siteName = model.siteName;
      this.devices = [];
      this.ignoredDeviceIds = model.ignored ? [...model.ignored] : [];
      this.emails = model.emails ? [...model.emails] : [];
      this.reportSchedule = model.schedules ? [...model.schedules] : [];
      this.internalOnly = (model.internalOnly || (this.id === -1 && this.$store.getters.isKgbAdminUser)) ? 'true' : 'false';
      this.includedUsageTypes = this.availableUsageTypes.map(x => x.key)
        .filter(x => !model.ignoredUsageTypes || !model.ignoredUsageTypes.includes(x));
      this.populateDeviceList(this.organization_id);
      this.populateSites();
      // Sets the display for the selected emails
      this.resetFields();
    },

    resetFields() {
      this.scheduleType = '';
      this.selectedDayOfMonth = '';
      this.addEmail = '';
      this.selectedDaysOfWeek = [];
    },

    /** On Change of Organzation */
    updateOrganization() {
      this.ignoredDeviceIds = [];
      this.populateDeviceList(this.organization_id);
      this.populateSites();
    },

    populateDeviceList(organizationId) {
      if (!organizationId) return;

      this.devices = [];
      this.userClient.getDevices(organizationId).then((devices) => {
        this.devices = devices.map(d => ({ ...d, onReport: this.ignoredDeviceIds.includes(d.id) ? 'false' : 'true' }));
      }).catch(() => {
        this.devices = [];
      });
    },

    addEmailAddress() {
      this.emails = [...this.emails, this.addEmail];
      this.addEmail = '';
    },

    validSchedule() {
      if (this.scheduleType === 'day') {
        return Number(this.selectedDayOfMonth) < 32 && Number(this.selectedDayOfMonth) > 0;
      }
      if (this.scheduleType === 'weekly') {
        return this.selectedDaysOfWeek.length > 0;
      }
      if (this.scheduleType === 'instant') {
        return true;
      }

      return false;
    },
    validEmail() {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return this.addEmail && re.test(this.addEmail);
    },

    deleteEmail(email) {
      this.emails = this.emails.filter(x => x !== email);
    },

    parseScheduleText(scheduleItem) {
      switch (scheduleItem.scheduleType) {
        case 'day':
          return `${scheduleItem.value} of month`;
        case 'weekly':
          return `Weekly on ${scheduleItem.value.split(',').map(d => this.daysOfWeek.find(x => x.value === Number(d)).text).join(', ')}`;
        case 'instant':
          return 'Instantly';
        default:
          return scheduleItem.value;
      }
    },

    addSchedule() {
      if (this.scheduleType === 'instant') {
        this.includedUsageTypes = ['download'];
      }

      this.reportSchedule = [
        ...this.reportSchedule,
        { id: Math.floor(Math.random() * 10000) * -1, scheduleType: this.scheduleType, value: (this.scheduleType === 'weekly') ? this.selectedDaysOfWeek.join(',') : this.selectedDayOfMonth },
      ];
      this.scheduleType = '';
      this.selectedDayOfMonth = '';
      this.selectedDaysOfWeek = [];
    },

    deleteSchedule(scheduleItem) {
      this.reportSchedule = this.reportSchedule.filter(x => x.id !== scheduleItem.id);
    },

    populateSites() {
      if (!this.organization_id) return;

      this.sites = null;
      this.userClient.getSitesForOrganization(this.organization_id)
        .then((sites) => { this.sites = [...defaultSites, ...sites]; });
    },
  },
};
</script>

<style scoped lang="scss">
.form__input {
  min-width: 150px;
  margin-right: 12px;
}

.inline-button {
  margin-bottom: 10px;
}

.checkbox-holder {
  padding: 6px 0;
}
</style>
